import { CheckCircleIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Box, Flex, Text } from "@chakra-ui/react";
import { formatInteger } from "utils/formatting";
import BrandStepDescription from "./BrandStepDescription";

enum SignupStepStatus {
  Active = "active",
  Complete = "complete",
  Pending = "pending",
}

const SIGNUP_STEPS = [
  {
    label: "User",
    description:
      "Create your login details first. Next, you’ll set up your brand workspace.",
  },
  {
    label: "Brand",
    description: <BrandStepDescription />,
  },
  {
    label: "Plan",
    description: "Choose a plan before opening your workspace.",
  },
] as const;

type Props = {
  currentStepIndex: number;
};

function getStepStatus(index: number, currentStepIndex: number) {
  if (index === currentStepIndex) return SignupStepStatus.Active;
  if (index < currentStepIndex) return SignupStepStatus.Complete;
  return SignupStepStatus.Pending;
}

export default function SignupStepIndicator({ currentStepIndex }: Props) {
  const { description } = SIGNUP_STEPS[currentStepIndex];

  return (
    <Box
      mb={8}
      sx={{
        "[data-step-status='active'] .signup-step-number": {
          backgroundColor: "magenta.veryDark",
          borderColor: "magenta.veryDark",
          color: "white",
        },
        "[data-step-status='active'] .signup-step-label": {
          color: "magenta.veryDark",
          fontWeight: 600,
        },
        "[data-step-status='complete'] .signup-step-number": {
          borderColor: "transparent",
          color: "green.500",
        },
        "[data-step-status='complete'] .signup-step-label": {
          color: "green.600",
        },
      }}
    >
      <Flex alignItems="center" gap={3} mb={3}>
        {SIGNUP_STEPS.map((step, index) => {
          const stepStatus = getStepStatus(index, currentStepIndex);
          const isComplete = stepStatus === SignupStepStatus.Complete;

          return (
            <Flex
              key={step.label}
              alignItems="center"
              data-step-status={stepStatus}
              gap={3}
              minWidth={0}
            >
              {index > 0 && <ChevronRightIcon boxSize={4} color="gray.300" />}

              <Flex alignItems="center" gap={2}>
                <Flex
                  alignItems="center"
                  borderColor="gray.300"
                  borderRadius="full"
                  borderWidth="1px"
                  className="signup-step-number"
                  color="gray.500"
                  fontSize="xs"
                  fontWeight={600}
                  height={6}
                  justifyContent="center"
                  width={6}
                >
                  {isComplete ? (
                    <CheckCircleIcon boxSize={6} />
                  ) : (
                    formatInteger(index + 1)
                  )}
                </Flex>

                <Text
                  className="signup-step-label"
                  color="gray.500"
                  fontSize="sm"
                >
                  {step.label}
                </Text>
              </Flex>
            </Flex>
          );
        })}
      </Flex>

      <Box color="gray.500" fontSize="sm">
        {description}
      </Box>
    </Box>
  );
}
